import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import moment from 'moment';
import useAuth from '../../../../features/hooks/useAuth';

import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import UndoIcon from '@mui/icons-material/Undo';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

// Styled Components
const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', sans-serif;
`;

// Basic Details section
const BasicDetailsCard = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 30px;
  padding: 20px;
  border-top: 4px solid #1976d2; /* Changed back to blue to match UnivReqFlow */
`;

const DetailsTitle = styled.h3`
  margin: 0 0 15px 0;
  color: #333;
  font-size: 18px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 10px;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const DetailItem = styled.div`
  margin-bottom: 10px;
`;

const DetailLabel = styled.div`
  font-weight: 500;
  color: #666;
  margin-bottom: 5px;
  font-size: 14px;
`;

const DetailValue = styled.div`
  color: #333;
  font-weight: 500;
`;

// Timeline components
const TimelineCard = styled.div`
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  padding: 20px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  background-color: ${props => {
    if (props.status === 'rejected') return '#fff8f8';
    if (props.status === 'accepted' || props.status === 'approved') return '#f1f8e9';
    return props.isEven ? '#f8f9fa' : '#fff';
  }};
  border-left: 3px solid ${props => {
    if (props.status === 'pending') return '#f57c00';
    if (props.status === 'rejected') return '#d32f2f';
    if (props.status === 'accepted' || props.status === 'approved') return '#4caf50';
    return '#1976d2';
  }};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 3px 8px rgba(0,0,0,0.1);
  }
`;

const TimelineContainer = styled.div`
  margin-top: 24px;
  padding-left: 16px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 2px;
    background-color: #e0e0e0;
    z-index: 0;
  }
`;

const TimelineItemContainer = styled.div`
  position: relative;
  margin-bottom: 20px;
  
  &::before {
    content: '';
    position: absolute;
    left: -20px;
    top: 24px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #1976d2; /* Changed back to blue to match UnivReqFlow */
    z-index: 1;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const TimelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e0e0e0;
`;

const TimelineTitle = styled.div`
  font-size: 17px;
  font-weight: 500;
  color: #202124;
  letter-spacing: -0.01em;
`;

const TimelineTime = styled.div`
  font-size: 14px;
  color: #5f6368;
`;

const AssetDetails = styled.div`
  margin: 15px 0;
  padding: 15px;
  background: #f5f5f5;
  border-radius: 4px;
`;

const AssetTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  
  th, td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #e0e0e0;
  }
  
  th {
    background-color: #e3f2fd; /* Changed back to light blue to match UnivReqFlow */
    font-weight: 600;
  }
  
  tr:hover {
    background-color: #f5f5f5;
  }
`;

const Remark = styled.div`
  margin: 15px 0;
  padding: 15px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  
  h4 {
    margin: 0 0 10px 0;
    color: #333;
  }
`;

const UserInfo = styled.div`
  margin-top: 15px;
  padding: 10px;
  background: #e3f2fd; /* Changed back to light blue to match UnivReqFlow */
  border-radius: 4px;
  font-size: 14px;
`;

const StatusBadge = styled.div`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  background: ${props => {
    if (props.status === 'accepted' || props.status === 'approved' || props.status === 'closed') return '#e8f5e9';
    if (props.status === 'pending') return '#fff8f1';
    if (props.status === 'rejected') return '#ffebee';
    return '#f5f5f5';
  }};
  color: ${props => {
    if (props.status === 'accepted' || props.status === 'approved' || props.status === 'closed') return '#2e7d32';
    if (props.status === 'pending') return '#ed6c02';
    if (props.status === 'rejected') return '#d32f2f';
    return '#757575';
  }};
`;

const ChipContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const AssetChip = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 16px;
  background-color: #e3f2fd;
  border: 1px solid #1976d2;
  font-size: 12px;
  color: #1976d2;
`;

const ReturnReqFlow = ({ row }) => {
  // User authentication and context
  const { id } = useAuth();
  const user_current_office = useSelector((state) => state?.office?.off || '');
  
  // State management
  const [assetDetails, setAssetDetails] = useState({});
  const [loading, setLoading] = useState(false);

  // Check if the request is closed or approved based on multiple indicators
  const isRequestClosed = useMemo(() => {
    console.log("Request status check:", {
      curr_status: row?.curr_status,
      curr_sent_to_tag: row?.curr_sent_to_tag,
      has_approval: row?.req_data?.some(item => item.sent_to_tag === 'store_return_approved'),
      req_data_tags: row?.req_data?.map(item => item.sent_to_tag)
    });
    
    return (
      (row?.curr_status?.toLowerCase?.() === 'closed') ||
      (row?.status?.toLowerCase?.() === 'closed') ||
      (row?.req_status?.toLowerCase?.() === 'closed') ||
      row?.curr_sent_to_tag === 'store_return_approved' ||
      row?.req_data?.some(item => 
        item.sent_to_tag === 'store_return_approved' || 
        item.status === 'closed' || 
        item.action?.toLowerCase?.().includes('closed')
      )
    );
  }, [row]);
  
  // Check if request is approved by BO or Store
  const isRequestApproved = useMemo(() => {
    return row?.curr_sent_to_tag === 'store_return_approved' ||
           row?.req_data?.some(item => 
             item.sent_to_tag === 'bo_return_req_approved' || 
             item.sent_to_tag === 'store_return_approved'
           );
  }, [row]);

  // Note: No longer need to fetch asset details API since data is embedded

  // Process asset details from the embedded data structure
  useEffect(() => {
    // Check if row and assets_requested exist
    if (!row?.assets_requested || row.assets_requested.length === 0) {
      setAssetDetails({});
      setLoading(false);
      return;
    }

    setLoading(true);
    
    // Extract asset details from the new embedded data structure
    const assetRequests = row.assets_requested;
    const detailsMap = {};
    
    // Loop through all asset request entries and collect asset details
    assetRequests.forEach(request => {
      if (request.data && Array.isArray(request.data)) {
        // Data field now contains complete asset objects
        request.data.forEach(asset => {
          if (asset && asset.id) {
            // Map the new structure to match the expected format
            detailsMap[asset.id] = {
              _id: asset.id,
              id: asset.id,
              oem_tag: asset.oem_tag,
              org_tag: asset.org_tag || '', // fallback if not present
              name: asset.assetName,
              specification: asset.spec,
              purchase_date: asset.pur_date,
              warrantyexp_date: asset.wrty_date
            };
          }
        });
      }
    });
    
    setAssetDetails(detailsMap);
    setLoading(false);
  }, [row?.assets_requested]);

  // Calculate the current status icon
  const getStatusIcon = () => {
    if (isRequestClosed) {
      return <CheckCircleOutlineIcon style={{ color: '#4caf50' }} />;
    } else if (row?.req_data?.some(item => item.sent_to_tag === 'bo_return_req_rejected' || item.sent_to_tag === 'store_return_rejected')) {
      return <CancelOutlinedIcon style={{ color: '#f44336' }} />;
    }
  };

  // Get total assets count
  const getTotalAssetCount = () => {
    if (!row?.assets_requested || row.assets_requested.length === 0) {
      return 0;
    }
    
    let count = 0;
    row.assets_requested.forEach(request => {
      if (request.data && Array.isArray(request.data)) {
        count += request.data.length;
      }
    });
    
    return count;
  };

  return (
    <Container>
      {/* Basic Details Section */}
      <BasicDetailsCard>
        <DetailsTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* {getStatusIcon()} */}
            <span>Request Details</span>
            <StatusBadge status={
              isRequestClosed ? 'closed' : 
              row?.req_data?.some(item => item.sent_to_tag === 'bo_return_req_rejected' || item.sent_to_tag === 'store_return_rejected') ? 'rejected' : 
              isRequestApproved ? 'approved' : 
              'pending'
            }>
              {isRequestClosed ? (
                'CLOSED'
              ) : row?.req_data?.some(item => item.sent_to_tag === 'bo_return_req_rejected' || item.sent_to_tag === 'store_return_rejected') ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  REJECTED
                </span>
              ) : isRequestApproved ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <CheckCircleOutlineIcon fontSize="small" /> APPROVED
                </span>
              ) : 'PENDING'}
            </StatusBadge>
          </div>
        </DetailsTitle>
        <DetailsGrid>
          <DetailItem>
            <DetailLabel>Request ID</DetailLabel>
            <DetailValue>{row?.uniqueReqId}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Request Type</DetailLabel>
            <DetailValue>
              {row?.req_type?.includes('Ind_Asset') ? 'Individual Asset Return' : 'Office Asset Return'}
            </DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Status</DetailLabel>
            <DetailValue>
              <StatusBadge status={
                isRequestClosed ? 'closed' : 
                row?.req_data?.some(item => item.sent_to_tag === 'bo_return_req_rejected' || item.sent_to_tag === 'store_return_rejected') ? 'rejected' : 
                isRequestApproved ? 'approved' : 
                'pending'
              }>
                {isRequestClosed ? (
                  'CLOSED'
                ) : row?.req_data?.some(item => item.sent_to_tag === 'bo_return_req_rejected' || item.sent_to_tag === 'store_return_rejected') ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <CancelOutlinedIcon fontSize="small" /> REJECTED
                  </span>
                ) : isRequestApproved ? (
                  'APPROVED'
                ) : 'PENDING'}
              </StatusBadge>
            </DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Created On</DetailLabel>
            <DetailValue>
              {moment(row?.created_at).format('DD/MM/YYYY HH:mm')}
            </DetailValue>
          </DetailItem>
          
          <DetailItem>
            <DetailLabel>Return Store</DetailLabel>
            <DetailValue>{row?.req_store || 'N/A'}</DetailValue>
          </DetailItem>
          
          <DetailItem>
            <DetailLabel>Requester</DetailLabel>
            <DetailValue>
              {row?.req_creator_details || '-'} 
            </DetailValue>
          </DetailItem>
          
          <DetailItem>
            <DetailLabel>No. of Actions</DetailLabel>
            <DetailValue>{row?.req_data?.length}</DetailValue>
          </DetailItem>
        </DetailsGrid>
        
        {/* Return Assets Table - Updated for new structure */}
        {row?.assets_requested && row.assets_requested.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <DetailLabel style={{ fontSize: '16px', marginBottom: '10px' }}>Assets Being Returned</DetailLabel>
            {/* Reverse the array to show the most recent request at the top */}
            {[...row.assets_requested].reverse().map((assetRequest, reqIndex) => (
              <div key={reqIndex} style={{ marginBottom: '20px' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '8px 12px',
                  backgroundColor: (reqIndex === 0 && (isRequestClosed || isRequestApproved)) ? '#f1f8e9' : (reqIndex === 0 && row?.req_data?.some(item => 
                    item.sent_to_tag === 'bo_return_req_rejected' || 
                    item.sent_to_tag === 'store_return_rejected'
                  )) ? '#ffebee' : '#e3f2fd',
                  borderRadius: '4px 4px 0 0',
                  marginBottom: '0'
                }}>
                  <div><strong>Reference:</strong> Action {assetRequest.actionNo || (row.assets_requested.length - reqIndex)}</div>
                  <div>
                    <strong>{reqIndex === row.assets_requested.length - 1 ? "Submitted on: " : "Modification Submitted on: "}</strong>
                    {moment(assetRequest.time_stamp).format('DD/MM/YYYY HH:mm')}
                  </div>
                </div>
                
                {loading ? (
                  <div style={{ 
                    padding: '30px', 
                    textAlign: 'center', 
                    color: '#666',
                    backgroundColor: '#fff' 
                  }}>
                    Loading asset details...
                  </div>
                ) : Object.keys(assetDetails).length === 0 ? (
                  <div style={{ 
                    padding: '30px', 
                    textAlign: 'center', 
                    color: '#666',
                    backgroundColor: '#fff' 
                  }}>
                    No asset details available
                  </div>
                ) : (
                  <AssetTable>
                    <thead>
                      <tr>
                        <th style={{ 
                          backgroundColor: (reqIndex === 0 && (isRequestClosed || isRequestApproved)) ? '#e8f5e9' : (reqIndex === 0 && row?.req_data?.some(item => 
                            item.sent_to_tag === 'bo_return_req_rejected' || 
                            item.sent_to_tag === 'store_return_rejected'
                          )) ? '#ffebee' : '#e3f2fd',
                          width: '60px'
                        }}>Sl.No</th>
                        <th style={{ 
                          backgroundColor: (reqIndex === 0 && (isRequestClosed || isRequestApproved)) ? '#e8f5e9' : (reqIndex === 0 && row?.req_data?.some(item => 
                            item.sent_to_tag === 'bo_return_req_rejected' || 
                            item.sent_to_tag === 'store_return_rejected'
                          )) ? '#ffebee' : '#e3f2fd',
                          width: '120px'
                        }}>OEM Tag</th>
                        <th style={{ 
                          backgroundColor: (reqIndex === 0 && (isRequestClosed || isRequestApproved)) ? '#e8f5e9' : (reqIndex === 0 && row?.req_data?.some(item => 
                            item.sent_to_tag === 'bo_return_req_rejected' || 
                            item.sent_to_tag === 'store_return_rejected'
                          )) ? '#ffebee' : '#e3f2fd',
                          width: '120px'
                        }}>Asset Name</th>
                        <th style={{ 
                          backgroundColor: (reqIndex === 0 && (isRequestClosed || isRequestApproved)) ? '#e8f5e9' : (reqIndex === 0 && row?.req_data?.some(item => 
                            item.sent_to_tag === 'bo_return_req_rejected' || 
                            item.sent_to_tag === 'store_return_rejected'
                          )) ? '#ffebee' : '#e3f2fd',
                          width: '300px'
                        }}>Specifications</th>
                        <th style={{ 
                          backgroundColor: (reqIndex === 0 && (isRequestClosed || isRequestApproved)) ? '#e8f5e9' : (reqIndex === 0 && row?.req_data?.some(item => 
                            item.sent_to_tag === 'bo_return_req_rejected' || 
                            item.sent_to_tag === 'store_return_rejected'
                          )) ? '#ffebee' : '#e3f2fd',
                          width: '100px'
                        }}>Purchase Date</th>
                        <th style={{ 
                          backgroundColor: (reqIndex === 0 && (isRequestClosed || isRequestApproved)) ? '#e8f5e9' : (reqIndex === 0 && row?.req_data?.some(item => 
                            item.sent_to_tag === 'bo_return_req_rejected' || 
                            item.sent_to_tag === 'store_return_rejected'
                          )) ? '#ffebee' : '#e3f2fd',
                          width: '100px'
                        }}>Warranty Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assetRequest.data && Array.isArray(assetRequest.data) && assetRequest.data.map((assetObj, idx) => {
                        // Use the asset object directly from the embedded data
                        const asset = assetObj || {};
                        return (
                          <tr key={idx} style={{ 
                            backgroundColor: idx % 2 === 0 ? 
                              ((reqIndex === 0 && (isRequestClosed || isRequestApproved)) ? '#f1f8e9' : (reqIndex === 0 && row?.req_data?.some(item => 
                                item.sent_to_tag === 'bo_return_req_rejected' || 
                                item.sent_to_tag === 'store_return_rejected'
                              )) ? '#fff8f8' : '#f8f9fa') : 
                              ((reqIndex === 0 && (isRequestClosed || isRequestApproved)) ? '#e8f5e9' : (reqIndex === 0 && row?.req_data?.some(item => 
                                item.sent_to_tag === 'bo_return_req_rejected' || 
                                item.sent_to_tag === 'store_return_rejected'
                              )) ? '#ffebee' : '#ffffff')
                          }}>
                            <td>{idx + 1}</td>
                            <td>{asset?.oem_tag || '-'}</td>
                            <td>{asset?.assetName || '-'}</td>
                            <td>
                              {asset?.spec ? (
                                <div style={{ 
                                  fontSize: '12px',
                                  width: '100%',
                                  minWidth: '280px',
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: '8px 15px',
                                  alignItems: 'flex-start'
                                }}>
                                  {Object.entries(asset.spec || {})
                                    .filter(([key, value]) => value && value !== '')
                                    .map(([key, value], index) => (
                                      <span key={index} style={{ 
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        whiteSpace: 'nowrap',
                                        minWidth: 'fit-content'
                                      }}>
                                        <strong style={{ marginRight: '4px' }}>{key}:</strong> 
                                        <span>{value}</span>
                                      </span>
                                    ))}
                                </div>
                              ) : '-'}
                            </td>
                            <td>
                              {asset?.pur_date ? 
                                new Date(asset.pur_date).toLocaleDateString() : '-'}
                            </td>
                            <td>
                              {asset?.wrty_date ? 
                                new Date(asset.wrty_date).toLocaleDateString() : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </AssetTable>
                )}
                
                {/* Add comparison with previous version if this isn't the first request */}
                {reqIndex < row.assets_requested.length - 1 && (
                  <div style={{ 
                    padding: '8px 12px', 
                    backgroundColor: '#f5f5f5', 
                    fontSize: '13px',
                    borderRadius: '0 0 4px 4px',
                    marginBottom: '5px'
                  }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Changes from Previous Version:</div>
                    {(() => {
                      const currAssets = assetRequest.data || [];
                      const prevAssets = row.assets_requested[row.assets_requested.length - reqIndex - 2]?.data || [];
                      
                      // Extract OEM tags from current and previous asset objects
                      const currOemTags = currAssets.map(asset => asset?.oem_tag).filter(tag => tag);
                      const prevOemTags = prevAssets.map(asset => asset?.oem_tag).filter(tag => tag);
                      
                      // Calculate additions and removals based on OEM tags
                      const added = currOemTags.filter(tag => !prevOemTags.includes(tag)).length;
                      const removed = prevOemTags.filter(tag => !currOemTags.includes(tag)).length;
                      
                      return (
                        <div style={{ display: 'flex', gap: '15px' }}>
                          {added > 0 && (
                            <div style={{ color: '#2e7d32' }}>
                              <strong>+{added}</strong> asset{added !== 1 ? 's' : ''} added
                            </div>
                          )}
                          {removed > 0 && (
                            <div style={{ color: '#d32f2f' }}>
                              <strong>-{removed}</strong> asset{removed !== 1 ? 's' : ''} removed
                            </div>
                          )}
                          {added === 0 && removed === 0 && (
                            <div style={{ color: '#757575' }}>
                              No changes in assets, other details may have been updated
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </BasicDetailsCard>

      <DetailsTitle>Request Timeline</DetailsTitle>
      <TimelineContainer>
        {row?.req_data?.map((entry, index) => (
          <TimelineItemContainer key={index}>
            <TimelineCard 
              isEven={index % 2 === 1} 
              status={
                entry?.sent_to_tag === 'bo_return_req_rejected' ||
                entry?.sent_to_tag === "store_return_rejected" ? 'rejected' : 
                entry?.sent_to_tag === 'bo_return_req_approved' ||
                entry?.sent_to_tag === 'store_return_approved' ||
                entry?.sent_to_tag === 'ureq_store_return_approved' ? 'approved' : 
                'pending'
              }
            >
              <TimelineHeader>
                <TimelineTitle>Action {index + 1}</TimelineTitle>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <TimelineTime>
                    {moment(entry?.time_stamp).format('DD/MM/YYYY HH:mm')}
                  </TimelineTime>
                  <StatusBadge 
                    status={
                      entry?.sent_to_tag === 'bo_return_req_rejected' ||
                      entry?.sent_to_tag === "store_return_rejected" ? 'rejected' : 
                      entry?.sent_to_tag === 'bo_return_req_approved' ||
                      entry?.sent_to_tag === 'store_return_approved' ||
                      entry?.sent_to_tag === 'ureq_store_return_approved' ? 'approved' : 
                      'pending'
                    }
                  >
                    {console.log("Timeline entry tag:", entry?.sent_to_tag)}
                    {entry?.sent_to_tag === 'bo_return_req_rejected' ||
                     entry?.sent_to_tag === "store_return_rejected" ? (
                       <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                         <CancelOutlinedIcon fontSize="small" /> REJECTED
                       </span>
                     ) : 
                     entry?.sent_to_tag === 'bo_return_req_approved' ||
                     entry?.sent_to_tag === 'store_return_approved' ||
                     entry?.sent_to_tag === 'ureq_store_return_approved' ? (
                       <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                         <CheckCircleOutlineIcon fontSize="small" /> APPROVED
                       </span>
                     ) : 'PENDING'}
                  </StatusBadge>
                </div>
              </TimelineHeader>

              {entry?.action && (
                <div style={{ 
                  marginBottom: '15px', 
                  padding: '10px 12px', 
                  background: entry?.sent_to_tag === 'bo_return_req_rejected' ||
                              entry?.sent_to_tag === "store_return_rejected" ? '#ffebee' : 
                             entry?.sent_to_tag === 'bo_return_req_approved' ||
                             entry?.sent_to_tag === 'store_return_approved' ||
                             entry?.sent_to_tag === 'ureq_store_return_approved' ? '#e8f5e9' : 
                             '#f0f8ff',
                  borderRadius: '4px',
                  borderLeft: `3px solid ${
                    entry?.sent_to_tag === 'bo_return_req_rejected' ||
                    entry?.sent_to_tag === "store_return_rejected" ? '#d32f2f' : 
                    entry?.sent_to_tag === 'bo_return_req_approved' ||
                    entry?.sent_to_tag === 'store_return_approved' ||
                    entry?.sent_to_tag === 'ureq_store_return_approved' ? '#4caf50' : 
                    '#1976d2'
                  }`,
                  fontSize: '16px',
                  lineHeight: '1.5',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}>
                  {entry?.sent_to_tag === 'bo_return_req_rejected' ||
                   entry?.sent_to_tag === "store_return_rejected" ? 
                   <CancelOutlinedIcon style={{ color: '#d32f2f' }} /> : 
                   entry?.sent_to_tag === 'bo_return_req_approved' ||
                   entry?.sent_to_tag === 'store_return_approved' ||
                   entry?.sent_to_tag === 'ureq_store_return_approved' ? 
                   <CheckCircleOutlineIcon style={{ color: '#4caf50' }} /> : 
                   null
                  }
                  {entry.action}
                </div>
              )}

              <Remark>
                <h4>Remark</h4>
                <div dangerouslySetInnerHTML={{ __html: entry?.Text || '' }} />
              </Remark>

              <UserInfo>
                <div style={{ marginBottom: '5px' }}>
                  <strong>From:</strong> {entry?.sent_by?.name?.firstName || '-'} - {entry?.sent_by?.office || '-'} - {entry?.sent_by?.designation || '-'}
                </div>
                <div>
                  <strong>To:</strong> {entry?.sent_to?.name?.firstName || '-'} - {entry?.sent_to?.office || '-'} - {entry?.sent_to?.designation || '-'}
                </div>
              </UserInfo>
            </TimelineCard>
          </TimelineItemContainer>
        ))}
      </TimelineContainer>
    </Container>
  );
};

export default ReturnReqFlow; 